import { Mail, MessageCircle, MessageSquare, Mic, Users } from 'lucide-react-native';
import { StyleSheet } from 'react-native';
import { Box, HStack, Text, VStack } from '../../../components/primitives';
import { useTranslation } from '../../../contexts/TranslationContext';
import { colors } from '../../../theme/colors';
import { type Campaign, CampaignChannel } from '../types';
import { CampaignStatusBadge } from './CampaignStatusBadge';

const CHANNEL_ICONS: Record<CampaignChannel, typeof Mail> = {
  [CampaignChannel.EMAIL]: Mail,
  [CampaignChannel.SMS]: MessageSquare,
  [CampaignChannel.WHATSAPP]: MessageCircle,
  [CampaignChannel.VOICE]: Mic,
};

interface CampaignListItemProps {
  campaign: Campaign;
  onPress: (campaign: Campaign) => void;
}

export function CampaignListItem({ campaign, onPress }: Readonly<CampaignListItemProps>) {
  const { t } = useTranslation();
  const leadCount = campaign._count?.campaignLeads ?? campaign.totalLeads;

  return (
    <Box
      pressable
      onPress={() => onPress(campaign)}
      borderRadius="md"
      padding="md"
      backgroundColor={colors.background.secondary}
      style={styles.card}
    >
      <VStack spacing="sm">
        <HStack spacing="sm" align="center" justify="between">
          <Text variant="body" color={colors.text.primary} style={styles.name} numberOfLines={1}>
            {campaign.name}
          </Text>
          <CampaignStatusBadge status={campaign.status} />
        </HStack>

        {campaign.description && (
          <Text variant="caption" color={colors.text.tertiary} numberOfLines={2}>
            {campaign.description}
          </Text>
        )}

        <HStack spacing="md" align="center">
          <HStack spacing="xs" align="center">
            {campaign.channels.map((channel) => {
              const Icon = CHANNEL_ICONS[channel];
              return Icon ? (
                <Icon key={channel} size={13} color={colors.text.tertiary} strokeWidth={2} />
              ) : null;
            })}
          </HStack>

          <HStack spacing="xxs" align="center">
            <Users size={12} color={colors.text.tertiary} strokeWidth={2} />
            <Text variant="caption" color={colors.text.tertiary}>
              {leadCount} {t('campaigns.detail.leads').toLowerCase()}
            </Text>
          </HStack>

          {campaign.createdBy && (
            <Text variant="caption" color={colors.text.disabled} style={styles.meta}>
              {campaign.createdBy.fullName}
            </Text>
          )}
        </HStack>
      </VStack>
    </Box>
  );
}

const styles = StyleSheet.create({
  card: {
    borderWidth: 1,
    borderColor: colors.border.default,
  },
  name: {
    flex: 1,
    fontWeight: '600',
  },
  meta: {
    fontSize: 11,
  },
});
